import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { getBookingForCheckout, createCheckoutSession } from "@/lib/bookings.functions";
import { applyReferralCredit, getMyReferralSummary } from "@/lib/referrals.functions";
import { toast } from "sonner";
import { formatGBP } from "@/lib/pricing";
import { Gift, Loader2, ShieldCheck, Lock, RefreshCcw, BadgeCheck, Undo2 } from "lucide-react";

export const Route = createFileRoute("/_authenticated/jobs/$jobId/confirm")({
  head: () => ({ meta: [{ title: "Confirm & pay — Peopels" }, { name: "robots", content: "noindex" }] }),
  component: ConfirmPage,
});

type Booking = {
  id: string;
  status: string;
  payment_status: string;
  agreed_rate: number;
  estimated_duration_minutes: number | null;
  subtotal: number;
  helper_fee: number;
  customer_fee: number;
  total_amount: number;
  helper_payout: number;
  referral_credit_applied?: number | null;
  scheduled_start: string | null;
  job: { id: string; title: string; description: string; postcode: string } | null;
  helper: { id: string; full_name: string | null; avatar_url: string | null } | null;
};

function ConfirmPage() {
  const { jobId } = Route.useParams();
  const navigate = useNavigate();
  const fetchBooking = useServerFn(getBookingForCheckout);
  const checkout = useServerFn(createCheckoutSession);
  const fetchReferral = useServerFn(getMyReferralSummary);
  const applyCredit = useServerFn(applyReferralCredit);
  const [booking, setBooking] = useState<Booking | null>(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);
  const [creditBalance, setCreditBalance] = useState<number>(0);
  const [applyingCredit, setApplyingCredit] = useState(false);
  const [acceptedPolicy, setAcceptedPolicy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user) { navigate({ to: "/auth", search: { redirect: `/jobs/${jobId}/confirm` } }); return; }
      const { data } = await supabase
        .from("bookings")
        .select("id")
        .eq("job_id", jobId)
        .eq("customer_id", auth.user.id)
        .in("status", ["pending", "accepted"])
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (!data?.id) {
        if (!cancelled) { setLoading(false); }
        return;
      }
      try {
        const [res, ref] = await Promise.all([
          fetchBooking({ data: { bookingId: data.id } }),
          fetchReferral().catch(() => null),
        ]);
        if (!cancelled) {
          setBooking(res.booking as unknown as Booking);
          setCreditBalance(ref?.credit_balance ?? 0);
          void import("@/lib/funnel-track").then(({ track }) =>
            track("checkout_view", { booking_id: data.id, job_id: jobId }),
          );
        }
      } catch {
        // ignored — empty state
      } finally {
        if (!cancelled) setLoading(false);
      }

    })();
    return () => { cancelled = true; };
  }, [jobId, fetchBooking, fetchReferral, navigate]);

  async function onApplyCredit(amount: number) {
    if (!booking) return;
    setApplyingCredit(true);
    try {
      const res = await applyCredit({ data: { bookingId: booking.id, amount } });
      setBooking({
        ...booking,
        referral_credit_applied: res.applied,
        total_amount: res.new_total,
      });
      setCreditBalance(res.remaining_balance);
      toast.success(
        amount > 0
          ? `Applied ${formatGBP(res.applied)} credit`
          : "Credit removed",
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't apply credit");
    } finally {
      setApplyingCredit(false);
    }
  }

  async function onPay() {
    if (!booking) return;
    setPayError(null);
    setPaying(true);
    void import("@/lib/funnel-track").then(({ track }) =>
      track("checkout_started", { booking_id: booking.id, total: Number(booking.total_amount) }),
    );
    try {
      const res = await checkout({ data: { bookingId: booking.id } });

      if (res.url) {
        window.location.href = res.url;
      } else {
        throw new Error("Checkout could not be started. Please try again.");
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Could not start checkout. Please try again.";
      setPayError(
        msg.includes("STRIPE_SECRET_KEY")
          ? "Payments aren't fully configured yet. Please contact support — your booking is safe."
          : msg
      );
      setPaying(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main id="main-content" className="mx-auto w-full max-w-2xl flex-1 px-4 py-12 sm:px-6 lg:px-8">
        <h1 className="font-display text-3xl font-bold tracking-tight">Confirm & pay</h1>
        <p className="mt-2 text-muted-foreground">Your card is securely authorized now. Funds are held in Stripe escrow and only released to your Helper once you confirm the job is done.</p>

        {loading ? (
          <div className="mt-12 grid place-items-center" role="status" aria-live="polite">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label="Loading booking" />
          </div>
        ) : !booking ? (
          <div className="mt-10 rounded-2xl border border-dashed border-border bg-card p-10 text-center">
            <p className="text-muted-foreground">No pending booking found yet. We'll notify you the moment a Helper accepts.</p>
            <Button asChild className="mt-4"><Link to="/dashboard">Back to dashboard</Link></Button>
          </div>
        ) : (
          <div className="mt-8 space-y-6">
            <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">Step 1 of 2 · Review job</p>
                  <p className="mt-1 font-display text-lg font-semibold">{booking.job?.title}</p>
                </div>
                <Link to="/dashboard" className="shrink-0 text-xs font-medium text-primary underline-offset-4 hover:underline">
                  Edit
                </Link>
              </div>
              <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">{booking.job?.description}</p>
              <p className="mt-3 text-xs text-muted-foreground">
                {booking.helper?.full_name ?? "Your Helper"} · {booking.estimated_duration_minutes ?? 60} min · {booking.job?.postcode}
                {booking.scheduled_start && <> · {new Date(booking.scheduled_start).toLocaleString("en-GB")}</>}
              </p>
            </div>


            <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Step 2 of 2 · Confirm & pay</p>
              <h2 className="mt-1 font-display text-lg font-semibold">Price breakdown</h2>

              <dl className="mt-4 space-y-2 text-sm">
                <Row label="Helper rate" value={`${formatGBP(Number(booking.agreed_rate))} / hr`} />
                <Row label="Subtotal" value={formatGBP(Number(booking.subtotal))} />
                <Row label="Service fee (5%)" value={formatGBP(Number(booking.customer_fee))} />
                {Number(booking.referral_credit_applied ?? 0) > 0 && (
                  <Row
                    label="Referral credit"
                    value={`− ${formatGBP(Number(booking.referral_credit_applied))}`}
                  />
                )}
                <div className="my-2 border-t border-border" />
                <Row label="Total today" value={formatGBP(Number(booking.total_amount))} bold />
              </dl>

              {(creditBalance > 0 || Number(booking.referral_credit_applied ?? 0) > 0) && (
                <div className="mt-4 flex items-start justify-between gap-3 rounded-xl border border-primary/20 bg-primary/5 p-3 text-sm">
                  <div className="flex items-start gap-2">
                    <Gift className="mt-0.5 h-4 w-4 text-primary" />
                    <div>
                      <p className="font-medium text-foreground">
                        {Number(booking.referral_credit_applied ?? 0) > 0
                          ? `${formatGBP(Number(booking.referral_credit_applied))} credit applied`
                          : `You have ${formatGBP(creditBalance)} in referral credit`}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {Number(booking.referral_credit_applied ?? 0) > 0
                          ? "Reduces your total today."
                          : `Apply up to ${formatGBP(Math.min(creditBalance, Number(booking.subtotal) + Number(booking.customer_fee)))} now.`}
                      </p>
                    </div>
                  </div>
                  {Number(booking.referral_credit_applied ?? 0) > 0 ? (
                    <button
                      onClick={() => onApplyCredit(0)}
                      disabled={applyingCredit}
                      className="shrink-0 text-xs font-semibold text-primary underline-offset-4 hover:underline disabled:opacity-50"
                    >
                      Remove
                    </button>
                  ) : (
                    <button
                      onClick={() =>
                        onApplyCredit(
                          Math.min(
                            creditBalance,
                            Number(booking.subtotal) + Number(booking.customer_fee),
                          ),
                        )
                      }
                      disabled={applyingCredit}
                      className="shrink-0 text-xs font-semibold text-primary underline-offset-4 hover:underline disabled:opacity-50"
                    >
                      {applyingCredit ? "Applying…" : "Apply"}
                    </button>
                  )}
                </div>
              )}

              {/* Confidence block */}
              <div className="mt-5 grid gap-3 rounded-xl bg-accent/40 p-4 text-xs text-muted-foreground sm:grid-cols-2">
                <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-primary" /> Held in escrow until job completed</span>
                <span className="inline-flex items-center gap-1.5"><Undo2 className="h-3.5 w-3.5 text-primary" /> Refunds reviewed per Cancellation Policy</span>
                <span className="inline-flex items-center gap-1.5"><BadgeCheck className="h-3.5 w-3.5 text-primary" /> Funds held in Stripe escrow</span>
                <span className="inline-flex items-center gap-1.5"><Lock className="h-3.5 w-3.5 text-primary" /> 256-bit SSL encryption, PCI-DSS compliant via Stripe.</span>
              </div>

              {/* Cancellation & refund policy */}
              <details className="mt-4 rounded-xl border border-border bg-background/60 p-3 text-xs" open>
                <summary className="cursor-pointer font-medium text-foreground">Cancellation &amp; refund policy</summary>
                <ul className="mt-2 space-y-1.5 text-muted-foreground">
                  <li>• Once a Professional accepts a booking, they may reserve time and travel to the job.</li>
                  <li>• Platform and service fees are non-refundable.</li>
                  <li>• Cancelling an accepted booking without a valid reason may retain up to 50% of the booking value to cover reserved time, lost work, travel and admin costs.</li>
                  <li>• Valid reasons (duplicate booking, professional unavailable, safety concerns, emergencies, other reasonable situations) are reviewed by Peopels for a full or partial refund.</li>
                  <li>• Approved refunds reach your card in 5–10 business days via Stripe.</li>
                </ul>
                <p className="mt-2 text-muted-foreground">
                  Full terms: <Link to="/cancellation" target="_blank" className="underline">Cancellation Policy</Link>.
                </p>
              </details>

              <label className="mt-4 flex items-start gap-2 text-xs text-foreground">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-border accent-primary"
                  checked={acceptedPolicy}
                  onChange={(e) => setAcceptedPolicy(e.target.checked)}
                  aria-required="true"
                />
                <span>
                  I have read and accept the{" "}
                  <Link to="/cancellation" target="_blank" className="underline">Cancellation Policy</Link>{" "}
                  and the <Link to="/terms" target="_blank" className="underline">Terms of Service</Link>.
                </span>
              </label>
            </div>

            {payError && (
              <div role="alert" className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                <span className="flex-1">{payError}</span>
                <button onClick={onPay} className="inline-flex items-center gap-1 font-semibold underline">
                  <RefreshCcw className="h-3.5 w-3.5" /> Retry
                </button>
              </div>
            )}

            <Button
              size="lg" className="w-full"
              onClick={onPay}
              disabled={paying || booking.payment_status === "paid" || !acceptedPolicy}
              aria-busy={paying}
            >
              {paying ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Opening secure checkout…</>
                : booking.payment_status === "paid" ? "Paid"
                : !acceptedPolicy ? <><Lock className="mr-2 h-4 w-4" />Accept policy to continue</>
                : <><Lock className="mr-2 h-4 w-4" />Pay {formatGBP(Number(booking.total_amount))} securely</>}
            </Button>

            <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> Secured by Stripe</span>
              <span aria-hidden>·</span>
              <span>Visa · Mastercard · Amex · Apple Pay · Google Pay</span>
            </div>
            <p className="text-center text-xs text-muted-foreground">
              By continuing you agree to our <Link to="/terms" className="underline">Terms</Link>. Need help?{" "}
              <Link to="/contact" className="underline">Contact support</Link>.
            </p>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className={`flex items-baseline justify-between ${bold ? "font-display text-base font-semibold" : ""}`}>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-foreground">{value}</dd>
    </div>
  );
}
